import { _isEmpty, _isNotBlank, _notNil } from '@/littledash';
import type { AgentReportV1 } from '@/model/Agent.model';
import { statusToTextMap } from '@/referenceData/study/status';
import { DateUtils } from '@/utils/Date.utils';
import type { FC } from 'react';
import styles from './ScorecardSnapshot.module.scss';

interface ScorecardSnapshotProps {
  snapshot: NonNullable<AgentReportV1['snapshot']>;
}

/**
 * The study's code, status, start date and elapsed time. Its name identifies the report and lives in
 * the modal title, so it is deliberately not repeated here.
 *
 * Every field is optional, so a report missing one renders the rest rather than showing a gap, and a
 * snapshot with nothing to display renders nothing at all.
 */
const ScorecardSnapshot: FC<ScorecardSnapshotProps> = ({ snapshot }) => {
  // Status and start date arrive as the values the study record holds, so they are rendered through
  // the same helpers as everywhere else in the app rather than being shown as sent.
  const fields = [
    { label: 'Code', value: snapshot.code, element: 'code' },
    {
      label: 'Status',
      value: _notNil(snapshot.status) ? statusToTextMap[snapshot.status] : null,
      element: 'status',
    },
    // An empty fallback rather than the default "Invalid Date": an unparseable date is dropped from
    // the header by the filter below, the same as one the report never carried.
    {
      label: 'Start date',
      value: DateUtils.renderDate(snapshot.start_date, { defaultResponse: '' }),
      element: 'start-date',
    },
    { label: 'Elapsed', value: snapshot.elapsed, element: 'elapsed' },
  ].filter((field) => _isNotBlank(field.value));

  // A snapshot carrying only the study name has nothing to show here, since the name lives in the
  // title — rendering the wrapper anyway would leave its margin as a gap above the findings.
  if (_isEmpty(fields)) {
    return null;
  }

  return (
    <header className={styles.snapshot} data-test-component="ScorecardSnapshot">
      <dl className={styles.fields}>
        {fields.map((field) => (
          <div key={field.element} className={styles.field} data-test-element={field.element}>
            <dt className={styles.label}>{field.label}</dt>
            <dd className={styles.value}>{field.value}</dd>
          </div>
        ))}
      </dl>
    </header>
  );
};

export default ScorecardSnapshot;
